// Port of SuiteModel/Locks.swift: locking a document or folder against accidental change. A locked item's text,
// synopsis, notes and title don't change, and it isn't moved, trashed or converted; a folder's lock covers everything
// under it. Status, labels, tags and comments still change. The flag is the fragment's own (`locked`), so it syncs
// with it; a build from before locks ignores it.
import type { Arrangement } from './model/documents.ts';
import { tree as buildTree, type ArrangementTreeNode } from './model/arrangement.ts';

/** `ProjectDocument.effectiveLocks(own:)`: the fragments under lock — locked themselves, and everything under a locked
 *  folder in any arrangement but the Trash. */
export function effectiveLocks(arrangements: readonly Arrangement[], own: ReadonlySet<string>): Set<string> {
  const all = new Set(own);
  if (!own.size) return all;
  for (const a of arrangements) {
    if (a.role === 'trash') continue;
    const walk = (nodes: ArrangementTreeNode[], under: boolean) => {
      for (const n of nodes) {
        const here = under || (n.node.fragmentID !== undefined && own.has(n.node.fragmentID));
        if (here && n.node.fragmentID !== undefined) all.add(n.node.fragmentID);
        walk(n.children, here);
      }
    };
    walk(buildTree(a), false);
  }
  return all;
}

/** `ModelError.locked`: what the store says when a locked document (or one under a locked folder) would change. */
export class LockedError extends Error {
  constructor(readonly title: string) {
    super(`“${title || 'The document'}” is locked. Unlock it to change it.`);
    this.name = 'LockedError';
  }
}
